import { logger } from '@adornis/base/logging.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { subscribeMailTemplatesForInstitution } from '@adornis/buchungsheld-db/api/mail-templates.js';
// import '@adornis/bh-mail-editor/tr-mail-editor.js';
import { InstitutionMailTemplate } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { catchError, filter, map, Observable, of, switchMap } from 'rxjs';

/*
 * @element bh-dialog-free-form-mails
 */
@customElement('bh-dialog-free-form-mails')
export class BHDialogFreeFormMails extends TrDialog<boolean> {
  static override get element_name() {
    return 'bh-dialog-free-form-mails';
  }

  @property({ attribute: false }) institutionID = new RXController(this, undefined);
  @property({ attribute: false }) mailAddresses?: string[];
  @property({ attribute: false }) mailTemplateType!: string;
  @property({ attribute: false }) copyMailAddress!: string;
  @property({ attribute: false }) mailSendOperation!: (
    mailAddresses: string[],
    template: MailTemplate,
  ) => () => Observable<string> | Promise<any>;
  @property({ attribute: false }) mailTemplateConstructor!: (doc: PartialEntityData<MailTemplate>) => MailTemplate;
  @property({ attribute: false }) allowSelectingTemplate = true;

  @state() protected _showAllAddresses = false;

  @state() protected _chooseFromTemplates = false;

  @state() private readonly _mailTemplates = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID =>
        subscribeMailTemplatesForInstitution(institutionID)(InstitutionMailTemplate.allFields as any),
      ),
      filter(Boolean),
    ),
  );

  protected _mailTemplate!: MailTemplate;

  override connectedCallback(): void {
    super.connectedCallback();

    if (!this.mailTemplateType) throw new Error('Cannot use bh-dialog-free-form-mails without mailTemplateType');
    if (!this.mailSendOperation) throw new Error('Cannot use bh-dialog-free-form-mails without mailSendOperation');
    this._mailTemplate = this.mailTemplateConstructor({ emailType: this.mailTemplateType });
  }

  protected override content() {
    if (this._chooseFromTemplates) {
      return html`
        <x-flex vertical space-between end space="sm">
          <mail-template-select></mail-template-select>
          ${(() => {
            const templates = this._mailTemplates.value;
            console.log({ templates });
            if (!templates) return html`<tr-loader></tr-loader>`;
            return html`
              <x-flex space="sm">
                ${templates.map(
                  t =>
                    html`
                      <x-button
                        @click=${() => {
                          this._mailTemplate = t as any as MailTemplate;
                          this._chooseFromTemplates = false;
                        }}
                      >
                        <x-text> ${t.name} </text>
                      </x-button>
                    `,
                )}
              </x-flex>
            `;
          })()}
          <x-button mode="outline" @click=${() => (this._chooseFromTemplates = false)}>
            ${this._translation.translate('LOGIN_PAGE', 'back' as any)}
          </x-button>
        </x-flex>
      `;
    }

    return html`
      <x-flex space="lg">
        ${this._mailAddressDisplay()}
        ${
          this.allowSelectingTemplate
            ? html`
                <x-button mode="outline" @click=${() => (this._chooseFromTemplates = true)}>
                  <x-text> ${this._translation.translate('mail_template', 'use_template' as any)} </x-text>
                </x-button>
              `
            : nothing
        }
        <tr-mail-editor send-only .value=${this._mailTemplate}></tr-mail-editor>
        <x-flex horizontal space-between end space="sm">
          <x-button mode="outline" @click=${() => this.close(false)}> Abbrechen </x-button>
          <x-button icon="send" @click=${() => this._sendMails()}> Mail senden </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  protected _mailAddressDisplay() {
    return html`
      <x-flex space="sm">
        <x-text bold> Mail versenden an folgende Mail-Adressen (${this.mailAddresses?.length ?? 0}): </x-text>
        <x-flex horizontal wrap space="sm">
          ${(this.mailAddresses ?? [])
            .slice(0, !this._showAllAddresses ? 3 : undefined)
            .map(
              address => html` <x-text ${css({ textDecoration: 'underline', color: 'blue' })}> ${address}</x-text> `,
            )}
          ${
            !this._showAllAddresses && (this.mailAddresses?.length ?? 0) > 3
              ? html`
                  <x-button mode="text" @click=${() => (this._showAllAddresses = true)}>
                    ... ${this.mailAddresses!.length - 3} weitere Mail-Adressen anzeigen
                  </x-button>
                `
              : nothing
          }
        </x-flex>

        <!-- mail copy -->
        ${
          this.copyMailAddress
            ? html`
                <x-hr></x-hr>
                <x-text bold> Kopie versenden an folgende Mail-Adressen (1): </x-text>
                <x-text ${css({ textDecoration: 'underline', color: 'blue' })}> ${this.copyMailAddress} </x-text>
              `
            : nothing
        }
      </x-flex>
    `;
  }

  protected async _sendMails() {
    if (!this.mailAddresses?.length) return;

    const mailSendOperation = this.mailSendOperation;
    if (!mailSendOperation)
      throw new Error('Cannot happen as this is catched in connectedCallback (bh-dialog-free-form-mails)');
    try {
      const operation = this.mailSendOperation.bind(null)(this.mailAddresses, this._mailTemplate);

      if (operation instanceof Promise) {
        return operation
          .then(() => {
            void XSnackbar.show('E-Mail Versand erfolgreich');
            this.close(true);
          })
          .catch(err => {
            console.error(err);
            void TrDialog.alert(
              'Es gab ein Problem beim Versenden der Freitext-Mails. Bitte versuchen Sie es später erneut.',
            );
          });
      }

      const op = operation();
      if (op instanceof Promise) throw new Error('not implemented');
      const numMailsSent = op.pipe(
        map(
          numMailsSent =>
            `${numMailsSent} Mail${+numMailsSent > 1 ? 's' : ''} wurde${
              +numMailsSent > 1 ? 'n' : ''
            } bereits versandt.`,
        ),
        // TODO catch this in TrDialog.waitFor and do proper error handling
        catchError((err, obs) => {
          logger.error({ err });
          void TrDialog.alert(
            'Es gab ein Problem beim Versenden der Freitext-Mails. Bitte versuchen Sie es später erneut.',
          );
          return of('');
        }),
      );

      if (numMailsSent) await TrDialog.waitFor(numMailsSent);
      void XSnackbar.show('Mails wurden erfolgreich versandt.');
      this.close(true);
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert(
        (err as any).message ??
          'Es gab ein Problem beim Versenden der Freitext-Mails. Bitte versuchen Sie es später erneut.',
      );
    }
  }
}
